import React from "react";
import { Box, Chip, styled, Typography } from "@mui/material";
import { RichText } from "../../components/RichText";
import { LadderXpTime } from "./LadderXpTime";
import { SmallGoldIcon } from "./icons/SmallGoldIcon";
import { ResumeIcon } from "./icons/ResumeIcon";
import { ExitButton } from "./ExitButton";
import {
  ActionButton,
  FullScreen,
  LadderScreenTopBar,
  LadderImage,
  LadderStartScreenLayout,
  TopRightLabel,
} from "./styles";
import { LadderType } from "./LadderType";
import { LadderTitle } from "./LadderTitle";
import {
  isGameLadderType,
  isProfilerLadderType,
  isScoredLadderType,
} from "../utils/ladder-util";
import type { Ladder, LadderProgress } from "../types";

// Inline SVG replacement for Iconify "eva:checkmark-fill"
const CheckmarkIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9.86 18a1 1 0 0 1-.73-.32l-4.86-5.17a1 1 0 1 1 1.46-1.37l4.12 4.39 8.41-9.2a1 1 0 1 1 1.48 1.34l-9.14 10a1 1 0 0 1-.73.33z" />
  </svg>
);

const LadderStartScreenLoading = () => {
  return (
    <FullScreen>
      <LadderStartScreenLayout>
        <Typography variant="h3">Loading...</Typography>
      </LadderStartScreenLayout>
    </FullScreen>
  );
};

const CompletedSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 3,
  margin: 10,
});

const HasGoldContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 3,
  margin: 10,
});

export const LadderStartScreen = ({
  loading = false,
  ladder,
  ladderProgress,
  onClose,
  onCancel,
}: {
  loading?: boolean;
  ladder: Ladder;
  ladderProgress: LadderProgress | null;
  onClose: () => void;
  onCancel: () => void;
}) => {
  const lp = ladderProgress;
  const status = lp?.overallStatus || "NOT_STARTED";

  if (loading || !ladder) {
    return <LadderStartScreenLoading />;
  }

  const canGetGold = isScoredLadderType(ladder.ladderType);
  const isProfile = isProfilerLadderType(ladder.ladderType);
  const isGame = isGameLadderType(ladder.ladderType);

  const replayMessage = isProfile || isGame ? "Play Again" : "Review Ladder";

  const hasGold =
    canGetGold && lp && lp.highScore >= lp.maxScore && lp.maxScore > 0;

  const inProgress = status === "IN_PROGRESS";
  const isCompleted = status === "COMPLETED";
  const isNotStarted = !(inProgress || isCompleted);

  const { estimatedTime, experiencePoints } = ladder;

  // Format completion date with Intl.DateTimeFormat (replaces date-fns format)
  const formatCompleteDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(date);
    } catch {
      return dateStr;
    }
  };

  return (
    <FullScreen>
      <LadderScreenTopBar>
        <ExitButton onClick={onCancel} />
        <TopRightLabel>
          <Typography variant="body1">
            <LadderType ladder={ladder} />
          </Typography>
        </TopRightLabel>
      </LadderScreenTopBar>

      <LadderStartScreenLayout>
        <LadderTitle ladder={ladder} />

        <LadderXpTime
          experiencePoints={experiencePoints}
          estimatedTime={estimatedTime}
          status={status}
        />
        <LadderImage src={ladder.image?.url} alt={ladder.image?.alt} />
        <Typography
          variant="body1"
          sx={{ textAlign: "center" }}
          component="div"
        >
          <RichText value={ladder.shortDescription} />
        </Typography>

        {isCompleted && (
          <CompletedSection>
            <Chip label="Completed" color="success" icon={<CheckmarkIcon />} />
            {lp?.completeDate && (
              <Typography
                sx={{ fontStyle: "italic" }}
                variant="h6"
                color="success.dark"
              >
                You completed this ladder on{" "}
                {formatCompleteDate(lp.completeDate)}
              </Typography>
            )}
          </CompletedSection>
        )}

        {hasGold && (
          <HasGoldContainer>
            <SmallGoldIcon width={75} height={75} />
            <Typography color="warning.dark" variant="subtitle1">
              Perfect Score
            </Typography>
          </HasGoldContainer>
        )}

        {isNotStarted && (
          <ActionButton variant="contained" color="primary" onClick={onClose}>
            Start
          </ActionButton>
        )}

        {inProgress && (
          <ActionButton variant="contained" color="warning" onClick={onClose}>
            <ResumeIcon style={{ marginRight: 10 }} />
            Resume
          </ActionButton>
        )}

        {isCompleted && (
          <ActionButton variant="contained" color="primary" onClick={onClose}>
            {replayMessage}
          </ActionButton>
        )}
      </LadderStartScreenLayout>
    </FullScreen>
  );
};
